import { ActivityItem, ActivityType } from 'app/data/types/activity';
import { dayInZone } from 'app/shared/utils/format-activity';

/**
 * Deeplink route map (data-contract §3). There is NO resolve endpoint — the FE builds each webview
 * url client-side from a static per-type template + the action's `routingDetails` ids + the item's
 * `buildingUuid`, then hands it to `sdk.redirectToContent({ type: WEBVIEW })`.
 *
 * Each `build` returns `null` when a required id is missing, so a deeplink-mode action that can't be
 * routed is omitted rather than becoming a dead tap (the "actions degrade, never dead-tap" rule).
 */

type RoutingDetails = Record<string, string> | undefined;

interface DeeplinkConfig {
  /** FE .env key holding the base url for the module landing (empty-state CTAs, fallbacks). */
  envKey: string;
  /** Base url key for the per-RECORD deeplink, when the record lives in a different miniapp. */
  recordEnvKey?: string;
  /**
   * Deep route appended to the base, or `null` if the required ids aren't present.
   *
   * `item` is the record being routed to, for the builders that need more than ids — a visit's link
   * has to name the day it falls on, and a booking's has to name the tab its list is on.
   */
  build: (
    routingDetails: RoutingDetails,
    buildingUuid: string | undefined,
    actionType: string,
    item?: Pick<ActivityItem, 'timestamp' | 'endTimestamp' | 'statusKey' | 'context'>,
  ) => string | null;
  /**
   * The route for a host that frames the record in a COLUMN beside the feed rather than giving it a
   * page of its own — the tenant portal's split surface. Optional: a type with no entry uses `build`
   * on every surface, which is what every type did before resource bookings needed the distinction.
   */
  buildInPane?: (routingDetails: RoutingDetails) => string | null;
}

const req = (details: RoutingDetails, key: string): string | undefined => details?.[key];

/**
 * The tenant's own bookings page in miniapp-resource-booking, and the booking on it.
 *
 * The page takes the booking as a QUERY parameter rather than a path segment: it has no route of its
 * own for one booking, and `?transactionUuid=` is the door it already opens one through
 * (`use-restore-selected-booking`). The transaction uuid is the only identifier that resolves
 * through a single-record fetch, so it is also the only one that can name a booking already past.
 *
 * This is where a booking opens on every surface that gives a record a PAGE — the portal's header
 * popover and both phone shells. The portal upgrades the same url to its own `bookings/:uuid` route;
 * see `buildInPane` below for the one surface that gets something else.
 */
export const RESOURCE_BOOKING_BOOKINGS_ROUTE = 'bookings';

/** The single-booking screen, which is what a COLUMN frames. See `buildInPane`. */
export const RESOURCE_BOOKING_RECEIPT_ROUTE = 'receipt';

/** The two lists the resource-booking app's bookings page renders. Its own `BookingsTabId`. */
type BookingsTab = 'upcoming' | 'past';

/**
 * How long helix keeps a FINISHED booking on the Upcoming list.
 *
 * Verified in `transaction.repository.ts` — `getWhereOptionsIncludesUpcomingTransactions` filters
 * `resource_booking_details.end_at >= now - 30 minutes`, off `getDateThirtyMinutesAgo()`. A booking
 * that ended twenty minutes ago is still on Upcoming, which is why "has it ended" and "is it past"
 * are different questions and only the second one answers this.
 *
 * ⚠️ A CONSTANT OWNED BY ANOTHER SERVICE, so treat it as approximate rather than authoritative. It is
 * safe to be wrong here in one direction only, and the expression below is arranged so that being
 * wrong means saying `upcoming` — which is the bookings page's own default, i.e. today's behaviour.
 */
const UPCOMING_GRACE_MS = 30 * 60_000;

/**
 * The booking statuses that are off Upcoming WHATEVER the clock says.
 *
 * helix admits a booking to Upcoming only while its latest status is in the set the CALLER asked for,
 * and its end is no more than 30 minutes past (`getWhereOptionsIncludesUpcomingTransactions`). So a
 * cancelled booking next Tuesday is on Past today — the thing a purely time-based test gets wrong.
 *
 * ⛔ DELIBERATELY NOT `VALID_RESOURCE_BOOKING_STATUSES`, helix's DEFAULT set, which the bookings page
 * overrides: it asks for `Pending, Accepted, Pending_Payment, Denied_Available, Denied_Unavailable`
 * (RES-1174, `TRANSACTION_STATUSES` in resource-booking's `api-client.ts`). A DENIED booking is
 * therefore still on Upcoming while its end is ahead, and only the clock takes it off — which is why
 * `denied` is not listed below, unlike every other ending.
 *
 * ⛔ DELIBERATELY NOT `COMPLETED_STATUS_KEYS` either, which looks like the same set and is not: it
 * holds `checked_in`, and a booking the tenant has checked into is one they are sitting in. That is
 * ACCEPTED to helix and squarely on Upcoming.
 *
 * ⚠️ A TERMINAL list, not a live one, so an unrecognised key falls through to the clock rather than
 * to Past. A status this build has never heard of is far more likely to be a new live state than a
 * new ending, and the clock is right about both far more often than a guess would be.
 */
const BOOKING_OFF_UPCOMING_STATUS_KEYS = new Set(['cancelled', 'no_show', 'expired', 'completed']);

/**
 * Which of the bookings page's two lists holds this booking — `undefined` when there is no item to
 * ask, in which case the link names no tab and the page falls back to what it remembers.
 *
 * ⭐ THE SENDER IS THE ONLY SIDE THAT CAN ANSWER THIS. The bookings page cannot work it out for
 * itself: `is_upcoming` is fixed in the LIST url and the single-record read carries no equivalent
 * field, so a booking fetched by uuid says nothing about which list it came from. Nor can the page
 * infer it from what it has loaded — Upcoming is paginated and Past is lazy, so "not in the list"
 * means "not fetched yet" as often as it means "not there".
 *
 * We can answer it because this feed carries the two things helix filters on.
 */
export const bookingsTabFor = (
  item?: Pick<ActivityItem, 'timestamp' | 'endTimestamp' | 'statusKey'>,
  now: number = Date.now(),
): BookingsTab | undefined => {
  if (!item) return undefined;

  if (BOOKING_OFF_UPCOMING_STATUS_KEYS.has(item.statusKey)) return 'past';

  /* `endTimestamp` is set only where the occurrence has a distinct end; every booking has one, but
   * a projection that stopped sending it would otherwise read as "ended at the start time". */
  const endsAt = new Date(item.endTimestamp ?? item.timestamp).getTime();

  /* An unparseable date is no answer at all, and `NaN` comparisons are false in both directions --
   * which would read as `upcoming` silently. Say nothing instead and let the page decide. */
  if (Number.isNaN(endsAt)) return undefined;

  return endsAt < now - UPCOMING_GRACE_MS ? 'past' : 'upcoming';
};

/**
 * `?tab=` rides ALONGSIDE `?transactionUuid=`, not instead of it. The uuid opens the booking — a
 * single-record read, so it works whichever list the booking is on — and the tab decides the list
 * waiting behind it when the reader closes it.
 *
 * Omitted rather than guessed when there is no item: the param is a tolerant contract, and a link
 * that names no tab is a link the page answers from its own memory, exactly as before.
 */
const bookingsRoute = (transactionUuid: string, tab?: BookingsTab): string => {
  const uuid = `transactionUuid=${encodeURIComponent(transactionUuid)}`;

  return `${RESOURCE_BOOKING_BOOKINGS_ROUTE}?${uuid}${tab ? `&tab=${tab}` : ''}`;
};

export const DEEPLINK_CONFIG: Record<ActivityType, DeeplinkConfig> = {
  event: {
    envKey: 'REACT_APP_SERVICE_BOOKING_URL',
    // A record has to open in miniapp-service-booking: it reads the event from SBS
    // (`/service-booking/v1/events/:uuid`), where the legacy miniapp looks the uuid up as a legacy
    // service via helix v3 and 404s. The landing stays on the legacy base — the new miniapp has no
    // root route.
    recordEnvKey: 'REACT_APP_EVENTS_MINIAPP_URL',
    build: (d, buildingUuid) => {
      const eventUuid = req(d, 'eventUuid');
      if (!eventUuid) return null;

      // A session booking opens its own slot, which needs the provider (and the building, so the page
      // lands in one hop rather than bouncing through a redirect).
      const companyUuid = req(d, 'companyUuid');
      const timeSlotUuid = req(d, 'timeSlotUuid');
      if (companyUuid && timeSlotUuid) {
        const prefix = buildingUuid ? `building/${buildingUuid}/` : '';
        return `${prefix}providers/${companyUuid}/events/${eventUuid}/slots/${timeSlotUuid}`;
      }

      // Otherwise let the miniapp resolve it: `/deeplink/:eventUuid` reads the provider and building
      // off the event, which is the only route legacy documents (no companyUuid) can take.
      return `deeplink/${eventUuid}`;
    },
  },
  resource_booking: {
    envKey: 'REACT_APP_RESOURCE_BOOKING_URL',
    build: (d, _buildingUuid, actionType, item) => {
      const transactionUuid = req(d, 'transactionUuid');
      const resourceUuid = req(d, 'resourceUuid');
      // "book again" routes to the resource details page; anything else opens the booking.
      // Select by the action type, not id-priority, so an action carrying BOTH ids routes correctly.
      // NOTE: book_again is handled separately by `useBookAgain` (it needs to resolve the resource's
      // real uuid + add locationId/singleAppInstanceConfig/skipRedirect), so this branch is unused.
      if (actionType === 'book_again') return resourceUuid ? `resources/${resourceUuid}` : null;
      if (transactionUuid) return bookingsRoute(transactionUuid, bookingsTabFor(item));
      if (resourceUuid) return `resources/${resourceUuid}`;
      return null;
    },
    /*
     * A column gets the receipt instead, because the bookings page is a LIST and a column is not
     * where a list beside another list belongs. The page would also arrive at the same screen a hop
     * later — in a narrow frame it resolves `?transactionUuid=` and opens the receipt itself — so
     * the pane would pay a flash of the list for the same destination.
     */
    buildInPane: (d) => {
      const transactionUuid = req(d, 'transactionUuid');
      /* Encoded for the same reason `bookingsRoute` encodes it, and more so: this one is a PATH
       * segment, where a stray `/`, `?` or `#` does not merely corrupt a value but renames the
       * route. The id arrives from the feed, not from us. */
      return transactionUuid ? `${RESOURCE_BOOKING_RECEIPT_ROUTE}/${encodeURIComponent(transactionUuid)}` : null;
    },
  },
  work_order: {
    envKey: 'REACT_APP_WORK_ORDERS_END_USER_URL',
    build: (d) => {
      const workOrderUuid = req(d, 'workOrderUuid');
      return workOrderUuid ? `${workOrderUuid}` : null;
    },
  },
  visitor_registration: {
    envKey: 'REACT_APP_VISITOR_REGISTRATION_URL',
    // `visits/:visitUuid` opens the visit itself: a modal over the visits list that describes whatever
    // state the visit is in, and fetches it by id so a deeplink lands without the list being visited
    // first. Replaces `visits/view/:id/confirm`, which was the post-create success screen — the only
    // detail route V2 had, so every visit arrived reading "Visit confirmed" with an offer to cancel it,
    // cancelled ones included.
    build: (d, buildingUuid, _actionType, item) => {
      const bUuid = req(d, 'buildingUuid') ?? buildingUuid;
      const visitUuid = req(d, 'visitUuid');
      if (!bUuid || !visitUuid) return null;

      /*
       * The day the visit falls on, for hosts that cannot open one visit.
       *
       * The miniapp opens the visit itself and ignores this. The tenant portal renders visitor
       * registration as its own MFE, which has no per-visit route — it shows a DAY — so it reads
       * this and lands on the right one. Without it the portal has no day to show and falls back to
       * the vertical home, losing the record the tap asked for.
       *
       * Resolved in the BUILDING's zone by the same helper that buckets the tile, so an evening
       * visit is filed on the day the card says it is.
       */
      const visitDate = item?.timestamp
        ? `&visitDate=${dayInZone(new Date(item.timestamp), item.context?.buildingTimezone)}`
        : '';

      return `visitor-registration/building/${bUuid}/visits/${visitUuid}?buildingUuid=${bUuid}${visitDate}`;
    },
  },
};
